import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { PAGE_LIMIT_MAX } from '@/lib/api/common'

import {
  createInventoryLocation,
  createStockItem,
  fetchInventoryCategories,
  fetchInventoryLocations,
  fetchInventoryUnits,
  fetchStockItems,
  fetchStockLevels,
  updateStockItem,
} from '../api'
import { InventoryCategoryStatus } from '../types'
import type {
  CreateLocationBody,
  CreateStockItemBody,
  InventoryLocationFilterValues,
  InventoryLocationQuery,
  InventoryUnit,
  StockItemQuery,
  StockLevel,
  UpdateStockItemBody,
} from '../types'

export const inventoryKey = ['inventory'] as const

/** `enabled: false` holds the request back — a search box with nothing typed in it yet. */
export function useStockItems(query: StockItemQuery, { enabled = true }: { enabled?: boolean } = {}) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...inventoryKey, 'items', query],
    queryFn: () => fetchStockItems(query),
    enabled,
    // Keeps the previous page on screen while the next one loads, so a filter
    // change does not blank the table in between.
    placeholderData: (previous) => previous,
  })

  return {
    items: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useStockItemActions() {
  const queryClient = useQueryClient()

  /* The new item lands at the top of the list (newest first), and its batch
     stock row with it — both read under `items`/`stock`, so both are refreshed. */
  const create = useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: CreateStockItemBody; idempotencyKey: string }) =>
      createStockItem(body, idempotencyKey),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'items'] }),
        queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'stock'] }),
      ]),
  })

  /* Every list holding the item is refreshed, so the next edit carries the new
     version — one made against the old would be refused as stale. So is the
     item read by id (`'item'`, singular — `useStockItemsById`), or a rename
     would not reach the recipes that name it as an ingredient. */
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateStockItemBody }) =>
      updateStockItem(id, body),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'items'] }),
        queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'item'] }),
      ]),
  })

  return { create, update }
}

/**
 * The active categories, for the filter.
 *
 * One page of the maximum rather than every page: a dropdown longer than a
 * hundred names has stopped being a dropdown, and the categories a tenant files
 * stock under number in the tens. They change rarely, so the list is held for a
 * few minutes rather than refetched on every visit.
 */
export function useInventoryCategories() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: [...inventoryKey, 'categories'],
    queryFn: () =>
      fetchInventoryCategories({
        status: InventoryCategoryStatus.Active,
        limit: PAGE_LIMIT_MAX,
        offset: 0,
      }),
    staleTime: 5 * 60_000,
  })

  return {
    categories: data?.rows ?? [],
    isLoading: isPending,
    // A failed list would otherwise read as "no categories" in every picker.
    error,
    retry: () => void refetch(),
  }
}

/**
 * The active locations — for the picker, and for turning a location id into a
 * name. Mirrored from the main API's branches, so they change about as often as
 * a business opens an outlet: held for ten minutes.
 */
export function useInventoryLocations() {
  const { data, isPending } = useQuery({
    queryKey: [...inventoryKey, 'locations'],
    queryFn: () => fetchInventoryLocations({ status: 'active', limit: PAGE_LIMIT_MAX, offset: 0 }),
    staleTime: 10 * 60_000,
  })

  return { locations: data?.rows ?? [], isLoading: isPending }
}

export function useLocationActions() {
  const queryClient = useQueryClient()

  /* Both the tab's list and the pickers' active list are read under
     `locations`, so the new warehouse shows up in each. */
  const create = useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: CreateLocationBody; idempotencyKey: string }) =>
      createInventoryLocation(body, idempotencyKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'locations'] }),
  })

  return { create }
}

/**
 * One location's stock for the items on screen, keyed by item id.
 *
 * Asked only once a location is picked and the page has rows. While the next
 * page loads, the previous page's levels stay up — but never another location's:
 * those are a different place's figures, and showing them under the new name for
 * even a moment would be showing wrong numbers.
 */
export function useStockLevels(itemIds: string[], locationId: string | null) {
  const enabled = Boolean(locationId) && itemIds.length > 0

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...inventoryKey, 'stock', locationId, itemIds],
    queryFn: () => fetchStockLevels({ locationId: locationId as string, itemIds }),
    enabled,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === locationId ? previous : undefined,
  })

  const levels = useMemo(
    () => new Map<string, StockLevel>((data?.rows ?? []).map((level) => [level.itemId, level])),
    [data],
  )

  return {
    levels,
    isLoading: enabled && isPending,
    isRefreshing: enabled && isFetching,
    error: enabled ? error : null,
    retry: () => void refetch(),
  }
}

/**
 * One page of locations for the Locations tab, closed ones included.
 *
 * Not `useInventoryLocations`, which is the picker's list: active only, one page
 * of the maximum. This is the register — a closed branch still holds the history
 * of what it held, and the tab is where that is looked up.
 */
/** The active units, by code — what every unit picker and conversion reads. */
export type UnitCatalog = ReadonlyMap<string, InventoryUnit>

/**
 * Every active unit, by code, for the forms' unit pickers and for converting a
 * recipe line into its ingredient's unit.
 *
 * One page of the maximum: the system ships nine and a business adds a handful.
 * Kept five minutes — units are set up once, not edited through the day.
 */
export function useUnitCatalog() {
  const { data, isPending, error } = useQuery({
    queryKey: [...inventoryKey, 'units', 'catalog'],
    queryFn: () => fetchInventoryUnits({ limit: PAGE_LIMIT_MAX, offset: 0, status: 'active' }),
    staleTime: 5 * 60_000,
  })

  const catalog = useMemo<UnitCatalog>(
    () => new Map((data?.rows ?? []).map((unit) => [unit.code, unit])),
    [data],
  )

  return { catalog, isLoading: isPending, error }
}

/**
 * Every unit, active or not, for the Units tab — which groups them by family, so
 * it needs them all at once rather than a page. One page of the maximum covers
 * it: the system ships ten and a business adds a handful.
 */
export function useInventoryUnits() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: [...inventoryKey, 'units', 'all'],
    queryFn: () => fetchInventoryUnits({ limit: PAGE_LIMIT_MAX, offset: 0 }),
    staleTime: 60_000,
  })

  return {
    units: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useInventoryLocationList(
  { type, status }: InventoryLocationFilterValues,
  page: number,
  pageSize: number,
) {
  const query: InventoryLocationQuery = {
    limit: pageSize,
    offset: page * pageSize,
    type: type ?? undefined,
    status: status ?? undefined,
  }

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...inventoryKey, 'locations', 'list', query],
    queryFn: () => fetchInventoryLocations(query),
    placeholderData: (previous) => previous,
  })

  return {
    locations: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}
